/**
 * @fileoverview API client wrappers for Universal Transactions feature.
 * @module features/universal-transactions/api/universalTransactions
 */

import apiClient from '@/lib/apiClient'
import { z } from 'zod'
import {
  IngestionResponseSchema,
  MappingConfigRequestSchema,
  MappingConfigResponseSchema,
  MappingConfigDetailSchema,
  PreviewResponseSchema,
  type IngestionResponse,
  type MappingConfigRequest,
  type MappingConfigResponse,
  type PreviewResponse,
  type MappingConfigDetail,
  type ReclassifyReport,
} from '../schemas/universal-transactions'
import type { Page } from '../schemas/universal-transactions'
import type { UniversalTransaction } from '../schemas/universal-transactions'

/** Base path for Universal Transactions API (relative to apiClient baseURL). */
const API_BASE = '/universal-transactions'

/**
 * Upload a CSV for preview. Returns header, first rows, totalStored, and optional suggested mapping info.
 * @param file CSV file to preview
 * @param sourceFileKey unique namespaced key for storage tracking
 * @returns PreviewResponse
 */
function normalizeCsvFile(file: File): File {
  // Some browsers or sources provide empty MIME type or 'application/vnd.ms-excel' for CSV.
  if (file.type === 'text/csv') return file
  return new File([file], file.name, { type: 'text/csv' })
}

export async function previewCsv(file: File, sourceFileKey: string): Promise<PreviewResponse> {
  const form = new FormData()
  const csv = normalizeCsvFile(file)
  form.append('file', csv, csv.name)

  const res = await apiClient.post(`${API_BASE}/preview`, form, {
    // Do not set Content-Type for FormData; the browser will set the correct boundary.
    params: { sourceFileKey },
  })
  return PreviewResponseSchema.parse(res.data)
}

/**
 * Refresh preview rows without re-uploading the file.
 * @param sourceFileKey source key used during preview upload
 * @param limit number of rows to fetch (default 10)
 */
export async function fetchPreviewRows(
  sourceFileKey: string,
  limit = 10
): Promise<PreviewResponse> {
  const res = await apiClient.get(`${API_BASE}/preview-rows`, { params: { sourceFileKey, limit } })
  return PreviewResponseSchema.parse(res.data)
}

/**
 * List recent mapping configurations (top 5 recent for the org).
 */
export async function listMappings(): Promise<MappingConfigResponse[]> {
  const res = await apiClient.get(`${API_BASE}/mappings`)
  const schema = z.array(MappingConfigResponseSchema)
  return schema.parse(res.data)
}

/**
 * Create a mapping configuration.
 */
export async function createMapping(request: MappingConfigRequest): Promise<MappingConfigResponse> {
  const body = MappingConfigRequestSchema.parse(request)
  const res = await apiClient.post(`${API_BASE}/mappings`, body)
  return MappingConfigResponseSchema.parse(res.data)
}

/**
 * Update an existing mapping configuration.
 */
export async function updateMapping(
  id: string,
  request: MappingConfigRequest
): Promise<MappingConfigResponse> {
  const body = MappingConfigRequestSchema.parse(request)
  const res = await apiClient.put(`${API_BASE}/mappings/${encodeURIComponent(id)}`, body)
  return MappingConfigResponseSchema.parse(res.data)
}

/**
 * Get a mapping configuration by id (full definition).
 */
export async function getMapping(id: string): Promise<MappingConfigDetail> {
  const res = await apiClient.get(`${API_BASE}/mappings/${encodeURIComponent(id)}`)
  return MappingConfigDetailSchema.parse(res.data)
}

/**
 * Delete a mapping configuration by id.
 */
export async function deleteMapping(id: string): Promise<void> {
  await apiClient.delete(`${API_BASE}/mappings/${encodeURIComponent(id)}`)
}

/**
 * Ingest a CSV using a mapping configuration.
 * @param file CSV file to ingest
 * @param mappingId mapping configuration ID
 */
export async function ingestCsv(file: File, mappingId: string): Promise<IngestionResponse> {
  const form = new FormData()
  const csv = normalizeCsvFile(file)
  form.append('file', csv, csv.name)

  const res = await apiClient.post(`${API_BASE}/ingest`, form, {
    // Do not set Content-Type for FormData; the browser will set the correct boundary.
    params: { mappingId },
  })
  return IngestionResponseSchema.parse(res.data)
}

export async function listUniversalTransactions(
  params: { page?: number; size?: number } = {}
): Promise<Page<UniversalTransaction>> {
  const { page = 0, size = 20 } = params
  const res = await apiClient.get(`${API_BASE}`, { params: { page, size } })
  // Try to parse a Page-like shape; be tolerant if backend wraps differently.
  const pageSchema = z.object({
    content: z.array(
      z.object({
        id: z.string(),
        orgId: z.string().optional(),
        txnDate: z.string(),
        originalAmount: z.number().optional(),
        originalCurrency: z.string().optional(),
        originalSignedAmount: z.number().optional(),
        reportingAmount: z.number().optional(),
        reportingCurrency: z.string().optional(),
        reportingSignedAmount: z.number().optional(),
        amount: z.number().optional(),
        direction: z.enum(['DEBIT', 'CREDIT']),
        signedAmount: z.number().optional(),
        currency: z.string().optional(),
        title: z.string().nullable().optional(),
        counterpartyName: z.string().nullable().optional(),
        fingerprint: z.string().optional(),
        rawSignature: z.string().optional(),
        extras: z.record(z.string(), z.unknown()).nullable().optional(),
        status: z.enum(['POSTED', 'PENDING']).nullable().optional(),
        accountId: z.string().nullable().optional(),
        accountIban: z.string().nullable().optional(),
        category: z.unknown().nullable().optional(),
        vatApplicable: z.boolean().nullable().optional(),
        reverseCharge: z.boolean().nullable().optional(),
        vatRate: z.number().nullable().optional(),
        originalVatAmount: z.number().nullable().optional(),
        reportingVatAmount: z.number().nullable().optional(),
        originalNetAmount: z.number().optional(),
        reportingNetAmount: z.number().optional(),
        reviewed: z.boolean().optional(),
        notes: z.string().nullable().optional(),
        invoiceStatus: z.string().optional(),
        costLinkingRequirement: z.string().optional(),
        createdAt: z.string(),
        linkedIndirectCostCount: z.number().optional(),
        linkingRequirementSatisfied: z.boolean().optional(),
      }).passthrough()
    ),
    totalElements: z.number().optional(),
    totalPages: z.number().optional(),
    size: z.number().optional(),
    number: z.number().optional(),
  }).passthrough()

  const parsed = pageSchema.safeParse(res.data)
  if (parsed.success) return parsed.data as Page<UniversalTransaction>

  // Fallback: best-effort normalization without failing the UI
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- fallback parsing for unexpected API responses
    const raw: any = res.data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- fallback parsing for unexpected API responses
    const items: any[] = Array.isArray(raw?.content) ? raw.content : []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- fallback parsing for unexpected API responses
    const normalized = items.map((it: any) => ({
      id: String(it.id ?? ''),
      orgId: it.orgId ?? undefined,
      txnDate: String(it.txnDate ?? ''),
      originalAmount: typeof it.originalAmount === 'number' ? it.originalAmount : undefined,
      originalCurrency: typeof it.originalCurrency === 'string' ? it.originalCurrency : undefined,
      originalSignedAmount:
        typeof it.originalSignedAmount === 'number' ? it.originalSignedAmount : undefined,
      reportingAmount: typeof it.reportingAmount === 'number' ? it.reportingAmount : undefined,
      reportingCurrency: typeof it.reportingCurrency === 'string' ? it.reportingCurrency : undefined,
      reportingSignedAmount:
        typeof it.reportingSignedAmount === 'number' ? it.reportingSignedAmount : undefined,
      amount: typeof it.amount === 'number' ? it.amount : undefined,
      direction: String((it.direction ?? '').toUpperCase()) === 'DEBIT' ? 'DEBIT' : 'CREDIT',
      signedAmount: typeof it.signedAmount === 'number' ? it.signedAmount : undefined,
      currency: typeof it.currency === 'string' ? it.currency : undefined,
      title: typeof it.title === 'string' ? it.title : null,
      counterpartyName:
        typeof it.counterpartyName === 'string' ? it.counterpartyName : undefined,
      fingerprint: typeof it.fingerprint === 'string' ? it.fingerprint : undefined,
      rawSignature: typeof it.rawSignature === 'string' ? it.rawSignature : undefined,
      extras: typeof it.extras === 'object' && it.extras !== null ? it.extras : {},
      status: it.status ?? null,
      accountId: it.accountId ?? null,
      accountIban: it.accountIban ?? null,
      category: it.category ?? null,
      vatApplicable: it.vatApplicable ?? null,
      reverseCharge: it.reverseCharge ?? null,
      vatRate: it.vatRate ?? null,
      originalVatAmount: it.originalVatAmount ?? null,
      reportingVatAmount: it.reportingVatAmount ?? null,
      originalNetAmount: typeof it.originalNetAmount === 'number' ? it.originalNetAmount : undefined,
      reportingNetAmount:
        typeof it.reportingNetAmount === 'number' ? it.reportingNetAmount : undefined,
      reviewed: typeof it.reviewed === 'boolean' ? it.reviewed : undefined,
      notes: it.notes ?? null,
      invoiceStatus: typeof it.invoiceStatus === 'string' ? it.invoiceStatus : undefined,
      costLinkingRequirement:
        typeof it.costLinkingRequirement === 'string' ? it.costLinkingRequirement : undefined,
      createdAt: String(it.createdAt ?? new Date().toISOString()),
      linkedIndirectCostCount:
        typeof it.linkedIndirectCostCount === 'number' ? it.linkedIndirectCostCount : undefined,
      linkingRequirementSatisfied:
        typeof it.linkingRequirementSatisfied === 'boolean'
          ? it.linkingRequirementSatisfied
          : undefined,
    }))
    return {
      content: normalized,
      totalElements: typeof raw.totalElements === 'number' ? raw.totalElements : undefined,
      totalPages: typeof raw.totalPages === 'number' ? raw.totalPages : undefined,
      size: typeof raw.size === 'number' ? raw.size : undefined,
      number: typeof raw.number === 'number' ? raw.number : undefined,
    } as Page<UniversalTransaction>
  } catch (e) {
    // As a last resort, avoid throwing to keep UI usable
    console.error('[universal-transactions] parse failed', e)
    return { content: [], totalElements: 0, totalPages: 0, size, number: page }
  }
}

/**
 * Re-run classification for a date range.
 */
export async function reclassify(startDate: string, endDate: string): Promise<number> {
  const res = await apiClient.post(`${API_BASE}/reclassify`, undefined, {
    params: { startDate, endDate },
  })
  const parsed = z.number().safeParse(res.data)
  if (!parsed.success) throw new Error('Invalid reclassify response')
  return parsed.data
}

/**
 * Batch reclassify with optional dry-run.
 */
export async function reclassifyBatch(params: {
  startDate: string
  endDate: string
  dryRun?: boolean
}): Promise<ReclassifyReport> {
  const { startDate, endDate, dryRun = true } = params
  const res = await apiClient.post(`${API_BASE}/reclassify/batch`, undefined, {
    params: { startDate, endDate, dryRun },
  })
  const { ReclassifyReportSchema } = await import('../schemas/universal-transactions')
  return ReclassifyReportSchema.parse(res.data)
}

/**
 * Manual override of classification/VAT fields for a single transaction.
 * Note: Response is ignored; callers should refetch list on success.
 */
export async function overrideUniversalTransaction(
  id: string,
  body: {
    vatApplicable?: boolean
    reverseCharge?: boolean
    vatRate?: number
    notes?: string
    invoiceStatus?:
      | 'NO_INFO'
      | 'INVOICE_PRESENT'
      | 'INVOICE_NOT_YET_AVAILABLE'
      | 'INVOICE_NOT_NEEDED'
  }
): Promise<void> {
  // Backend breaking change: classification updates now live under "/classification" subresource
  await apiClient.patch(`${API_BASE}/${encodeURIComponent(id)}/classification`, body)
}

export default {
  previewCsv,
  fetchPreviewRows,
  listMappings,
  createMapping,
  updateMapping,
  getMapping,
  deleteMapping,
  ingestCsv,
  listUniversalTransactions,
  reclassify,
  reclassifyBatch,
  overrideUniversalTransaction,
}
